<template>
    <b-modal
        @ok="handleCreateOk"
        @cancel="resetData"
        ref="create-modal"
        :title="$t('tournament.create_modal.title')"
        ok-title="Create"
        :cancel-title="$t('tournament.btn.cancel')"
        size="lg"
        scrollable>

      <h4 class="text-primary">{{ $t('tournament.create_modal.section_basic_params_title') }}</h4>

      <custom-form-input
        :label="$t('tournament.create_modal.label.name')"
        :input-type="'default'"
        :isValid="validateState('name')"
        translationName='tournament.create_modal.name'
        :isTooltipAvailable="false"
        v-model="data.name">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.wallet_currency')"
          :input-type="'select'"
          :isValid="validateState('walletCurrency')"
          translationName='tournament.create_modal.walletCurrency'
          :isTooltipAvailable="true"
          v-model="data.walletCurrency"
          :select-options="{options: walletCurrencies, textField: 'textField', valueField: 'valueField'}">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.question_category')"
          :input-type="'select'"
          :isValid="validateState('questionCategoryId')"
          translationName='tournament.create_modal.questionCategoryId'
          :isTooltipAvailable="true"
          v-model="data.questionCategoryId"
          :select-options="{options: questionCategories, textField: 'label', valueField: 'id'}">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.required_game_players')"
          :input-type="'select'"
          :isValid="validateState('maxGamePlayers')"
          translationName='tournament.create_modal.requiredGamePlayers'
          :isTooltipAvailable="true"
          v-model="data.maxGamePlayers"
          @input="getPrizeStructure(true)"
          :select-options="{options: maxGamePlayersOptions}">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.next_level_qualified_players')"
          :input-type="'select'"
          :isValid="validateState('winnerGamePlayersQuantity')"
          translationName='tournament.create_modal.nextLevelQualifiedPlayers'
          :isTooltipAvailable="true"
          v-model="data.winnerGamePlayersQuantity"
          @input="getPrizeStructure(true)"
          :select-options="{options: winnerGamePlayersQuantityOptions}">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.required_tournament_players')"
          :input-type="'select'"
          :isValid="validateState('winnerGamePlayersQuantity')"
          translationName='tournament.create_modal.requiredTournamentPlayers'
          :isTooltipAvailable="true"
          :disabled="disablePlayerNumbersInput"
          v-model="data.playerNumbers"
          @input="getPrizeStructure(true)"
          :select-options="{options: availablePlayers}">
      </custom-form-input>
      <p v-if="errorMessage" class="text-danger mb-3" style="margin-top: -10px;">{{ $t(errorMessage) }}</p>
      <custom-form-input
          :label="$t('tournament.create_modal.label.buy_in')"
          :input-type="'default'"
          :isValid="validateState('buyIn')"
          translationName='tournament.create_modal.buyIn'
          :isTooltipAvailable="true"
          v-model="data.buyIn">
      </custom-form-input>
      <div v-if="prizePoolStructure && prizePool" class="row">
        <div class="col-6">
          <ul>
            <li v-for="(item, index) in prizePoolStructure" :key="index" class="d-flex align-items-center justify-content-between mb-2">
              <div class="d-flex align-items-center">
                <span class="mr-2"><b>{{ item.placeToShow }}</b> {{ $t('tournament.create_modal.player_position') }}:</span>
                <b>({{ item.repeated }})</b> &nbsp; {{ $t('tournament.create_modal.player') }}
              </div>

              <b-form-checkbox
                  v-if="prizePool[index]"
                  :id="`checkbox-${index}`"
                  v-model="prizePool[index].checked"
                  :disabled="index === 0 || (prizePool[index-1] && !prizePool[index-1].checked) ? true : false"
                  @input="resetPrizePoolValue($event, index)"
                  @click="showAlert((prizePool[index-1] && !prizePool[index-1].checked))"
                  :name="`checkbox-${index}`"
                  class="ml-3"
              >
              </b-form-checkbox>

              <b-form-input
                  v-if="prizePool[index]"
                  @input="setPrizePoolValue($event, index)"
                  :state="!$v.prizePool.$each[index].$dirty ? null : !$v.prizePool.$each[index].$error"
                  v-model="$v.prizePool.$each[index].value.$model"
                  :disabled="!prizePool[index].checked"
                  class="w-25"
              ></b-form-input>
            </li>
          </ul>
        </div>
        <div class="col-6 p-3">
          <h3>{{ $t('tournament.create_modal.prize_pool_check') }}</h3>
          <div>{{ $t('tournament.create_modal.total_buy_in') }}: {{ totalBuyIn }}</div>
          <div>{{ $t('tournament.create_modal.total_prize_pool') }}: {{ totalPrizePool }}</div>
          <hr>
          <p :class="[totalCommission < 0 ? 'text-danger' : 'text-success']">{{ $t('tournament.create_modal.commission') }}: {{ totalCommission }}</p>
        </div>
      </div>

      <div v-if="prizePoolStructure && prizePool && data.quizRankingPoints && data.quizRankingPoints.length > 0"  class="p-4 mt-4 mb-4 bg-light">
        <h4 class="text-primary">{{ $t('tournament.create_modal.label.quiz_ranking_points') }}</h4>

        <div class="row">
          <div class="col-12">
            <ul>
              <li v-for="(item, index) in data.quizRankingPoints" :key="index" class="d-flex align-items-center justify-content-between mb-2">
                <div class="d-flex align-items-center">
                  <span class="mr-2"><b>{{item.position}}</b> {{ $t('tournament.create_modal.label.quiz_ranking_points_for_position') }}:</span>
                  <b>({{ prizePoolStructure[index].repeated }})</b> {{ $t('tournament.create_modal.label.quiz_ranking_points_players_count') }}
                </div>

                <b-form-input
                    v-model="data.quizRankingPoints[index].points"
                    @input="$v.data.quizRankingPoints.$each[index].points.$model = $event.trim()"
                    :state="!$v.data.quizRankingPoints.$each[index].points.$dirty ? null : !$v.data.quizRankingPoints.$each[index].points.$error"
                    class="w-25"
                ></b-form-input>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="p-4 mt-4 mb-4 bg-light">
        <h4 class="text-primary">{{ $t('tournament.create_modal.section_stages_params_title') }}</h4>

        <div class="row">
          <div class="col-sm-8">
            <b-form-select id="form-chooseRoundType"
                           v-model="addStageRoundType"
                           :options="roundTypes">
              <b-form-select-option value="null" disabled>{{ $t('select.placeholder') }}</b-form-select-option>
            </b-form-select>
          </div>
          <div class="col-sm-4">
            <button class="btn btn-success" @click="addStage">{{ $t('tournament.btn.add_stage') }}</button>
          </div>
        </div>
      </div>

      <div
          v-for="(stage,index) in data.stagesParameters"
          :key="index"
          class="p-4 "
          :style="{ background: index % 2 === 0 ? '#dee2e6' : '' }"
          :class="[index % 2 === 0 ? 'mb-0': 'mb-4' ]"
      >
        <StageConfiguration
            :ref="'ref-stage-'+index"
            :round-type="stage.roundType"
            :cms-pages-list="cmsPagesList"
            :clonedData="stage">
        </StageConfiguration>
        <button class="btn btn-danger" @click="removeStage(index)">{{ $t('tournament.btn.remove_stage') }}</button>
      </div>

      <h4 class="text-primary mt-4">{{ $t('tournament.create_modal.section_final_stage_params_title') }}</h4>

      <custom-form-input
          :label="$t('tournament.create_modal.label.points_multiplier')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','pointsMultiplier'])"
          translationName='tournament.create_modal.pointsMultiplier'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.pointsMultiplier">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.minimum_amount_of_time')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','minAmountOfTime'])"
          translationName='tournament.create_modal.minAmountOfTime'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.minAmountOfTime">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.number_of_rounds_to_change_time')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','numberOfRoundsToChangeTime'])"
          translationName='tournament.create_modal.numberOfRoundsToChangeTime'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.numberOfRoundsToChangeTime">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.cms_page_key_name')"
          :input-type="'select'"
          :isValid="validateState(['finalStageParameters','cmsPageKeyName'])"
          translationName='tournament.create_modal.cmsPageKeyName'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.cmsPageKeyName"
          :select-options="{options: cmsPagesList, textField: 'title', valueField: 'keyName'}">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.question_time_bonus')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','roundConfiguration','questionTimeBonus'])"
          translationName='tournament.create_modal.questionTimeBonus'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.roundConfiguration.questionTimeBonus">
      </custom-form-input>

      <custom-form-input
          :label="$t('tournament.create_modal.label.question_time_penalty')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','roundConfiguration','questionTimePenalty'])"
          translationName='tournament.create_modal.questionTimePenalty'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.roundConfiguration.questionTimePenalty">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.password')"
          :input-type="'default'"
          translationName='waiting_room.create_modal.password'
          :isValid="validateState('password')"
          :showUpperCaseValue="true"
          v-model="data.password">
      </custom-form-input>
    </b-modal>
</template>

<script>
import axios from 'axios';
import {helpers, integer, minValue, required, requiredIf} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";
import StageConfiguration from "../StageConfiguration";
import moment from "moment";
import CustomFormInput from "../../../components/Form/CustomFormInput";
import alertify from "alertifyjs";
// import customValidators from "../../../validators/customValidators";

//had to define own regex for fractions - built in types (decimal, numeric) didn't satisfy business requirements
 const fraction = helpers.regex('fraction', /^[-]?\d+([.,]\d+)?$/);
//had to define own regex for not negative fractions - built in minValue type crapped out in conjunction with fractions
const notNeg = helpers.regex('notNeg', /^[^-]/);

//TODO: import / load / get locale the right way
let locale = locale ? locale : "en";

export default {
  components: {StageConfiguration, CustomFormInput},
  props: ['apiKey'],
  mixins: [validationMixin],
  validations: {
    prizePool: {
      $each: {
        value: {
          required: requiredIf(prop => prop.checked === true),
          minValue: minValue(0)
        }
      }
    },
    data: {
      name: {required},
      questionCategoryId: {required},
      maxGamePlayers: {required, integer},
      playerNumbers: {required, integer},
      winnerGamePlayersQuantity: {required, integer},
      quizRankingPoints: {
        $each: {
          points: {
            required,
            minValue: minValue(0),
            integer
          }
        }
      },
      buyIn: {required, fraction, notNeg},
      walletCurrency: {required},
      finalStageParameters: {
        pointsMultiplier: {required, fraction, notNeg},
        minAmountOfTime: {required, integer, minValue: minValue(1)},
        numberOfRoundsToChangeTime: {required, integer, minValue: minValue(1)},
        cmsPageKeyName: { required },
        roundConfiguration: {
          questionTimeBonus: {required, integer, minValue: minValue(0)},
          questionTimePenalty: {required, integer, minValue: minValue(0)},
        },
      },
      password: {}
    }
  },
  data() {
    return {
      availablePlayers: [],
      addStageRoundType: null,
      data: this.getInitialData(),
      prizePool: null,
      prizePoolStructure: null,
      walletCurrencies: [
        {textField: this.$t('FUN_COINS'), valueField: 'FUN_COINS'},
        {textField: this.$t('QUIZ_COINS'), valueField: 'QUIZ_COINS'}
      ],
      maxGamePlayersOptions: [2, 4],
      winnerGamePlayersQuantityOptions: [1, 2],
      roundTypes: ['COLLECTING', 'SPRINTING', 'BETTING', 'CONQUERING'],
      cmsPagesList: [],
      questionCategories: [],
      errorMessage: null
    };
  },
  mounted() {
    axios.get('/api/question/question-category?limit=1000')
        .then((resp) => {
          this.questionCategories = resp.data.items.map(c => {
            return {
              id: c.id,
              label: this.$t('category.'+c.name)
            }
          });
        })
        .catch((err) => {
          console.log(err)
        })
    axios.get(`/api/resource/children/round-rules/${locale}`)
        .then((resp) => {
          this.cmsPagesList = resp.data[0].__children;
        })
        .catch((err) => {
          console.log(err)
        });
    this.$refs['create-modal'].show();
  },
  watch: {
    ['data.maxGamePlayers']() {
      if (this.data.maxGamePlayers && this.data.winnerGamePlayersQuantity)
        this.fetchAvailablePlayers();
    },
    ['data.winnerGamePlayersQuantity']() {
      if (this.data.maxGamePlayers && this.data.winnerGamePlayersQuantity) {
        this.fetchAvailablePlayers();
      }
    }
  },
  computed: {
    totalBuyIn() {
      return Number(this.data.buyIn) * Number(this.data.playerNumbers);
    },
    prizePoolToSend() {
      const prizePoolToSend = {};

      this.prizePool.forEach(item => {
        prizePoolToSend[item.property] = Number(item.value);
      });

      return prizePoolToSend;
    },
    totalPrizePool() {
      let count = 0;
      if(this.prizePool) {
        for (const [key, value] of Object.entries(this.prizePool)) {
          if(value.value) {
            count += Number(value.value) * Number(this.prizePoolStructure[key].repeated);
          }

        }
      }
      return count;
    },
    totalCommission() {
      return this.totalBuyIn - this.totalPrizePool;
    },
    disablePlayerNumbersInput() {
      return !this.availablePlayers.length || this.data.maxGamePlayers === null || this.data.winnerGamePlayersQuantity === null;
    }
  },
  methods: {
    showAlert(val) {
      if(val) {
        alertify.error('NOT')
      }
    },
    fetchAvailablePlayers() {
      const url = `/api/tournament/available-players?winnerGamePlayersQuantity=${this.data.winnerGamePlayersQuantity}&maxGamePlayers=${this.data.maxGamePlayers}`;

      axios.get(url)
        .then(resp => {
          this.errorMessage = null;
          this.availablePlayers = resp.data;
        })
        .catch((err) => {
          if(err.response && err.response.data) {
            this.errorMessage = this.$t(err.response.data.label);
          }
        });
    },
    resetPrizePoolValue(e, index) {
      if(!e) {
        this.prizePool.forEach((item,index2) => {
          if(index2 >= index) {
            this.prizePool[index2].value = null;
            this.prizePool[index2].checked = null;
          }
        })
      }
    },
    setPrizePoolValue(e, index) {
      if(this.prizePool[index].checked) {
        this.prizePool[index].value = e;
      } else {
        this.prizePool[index].value = 0;
      }
    },
    setInitialPrizePool(prizeLoopStructure) {
      const prizePool = [];
      // eslint-disable-next-line no-unused-vars
      let index = 0;

      // eslint-disable-next-line no-unused-vars
      for (const [key, value] of Object.entries(prizeLoopStructure)) {
        prizePool.push({
          property: value.placeToShow,
          playersCount: value.repeated,
          value: index === 0 ? 0 : null,
          checked: index === 0 ? true : false
        });

        index++;
      }

      this.prizePool = prizePool;

      if(this.data.setNewRankingPoints) {
        const quizRankingPoints = [];

        for(let i = 0; i <= Number(this.prizePoolStructure.length-1); i++) {
          quizRankingPoints[i] = {
            position: this.prizePoolStructure[i].placeToShow.toString(),
            points: 0
          };
        }

        this.data.quizRankingPoints = quizRankingPoints;
      }

      this.data.setNewRankingPoints = true;
    },
    getPrizeStructure(refreshPrizePool = false) {
      if(this.data.winnerGamePlayersQuantity && this.data.maxGamePlayers && this.data.playerNumbers) {
        const url = `/api/tournament/results-table?playerNumbers=${this.data.playerNumbers}&winnerGamePlayersQuantity=${this.data.winnerGamePlayersQuantity}&maxGamePlayers=${this.data.maxGamePlayers}`;

        return new Promise((resolve, reject) => {
          axios.get(url)
              .then(resp => {
                this.errorMessage = null;
                this.prizePoolStructure = resp.data;
                if(refreshPrizePool) {
                  this.setInitialPrizePool(this.prizePoolStructure);
                }
                resolve();
              })
              .catch((err) => {
                if(err.response && err.response.data) {
                  this.errorMessage = this.$t(err.response.data.label);
                }

                reject();
              });
        });
      }

    },
    validateState(entry) {
      if (entry.constructor.name === 'Array') {
        switch (entry.length) {
          case 3:
            entry = this.$v.data[entry[0]][entry[1]][entry[2]];
            break;
          case 2:
            entry = this.$v.data[entry[0]][entry[1]];
            break;
        }
      } else {
        entry = this.$v.data[entry];
      }
      const {$dirty, $error} = entry;
      return $dirty ? !$error : null;
    },
    addStage() {
      if (this.addStageRoundType !== null) {
        this.data.stagesParameters.push({
          stageOrder: this.data.stagesParameters.length + 1,
          roundType: this.addStageRoundType,
          roundConfiguration: {}
        });
      }
    },
    removeStage(index) {
      this.data.stagesParameters.splice(index, 1);
    },
    handleCreateOk(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.handleCreateSubmit()
    },
    handleCreateSubmit() {
      this.$v.$touch();

      let isValid = this.$v.$invalid === false;
      for (let i in this.data.stagesParameters) {
        let t = this.$refs['ref-stage-' + i][0].isValid();
        isValid = isValid && t;
      }
      if (isValid) {
        const rankingPoints = this.data.quizRankingPoints.map(item => {
          const obj = {position: item.position.toString(), points: Number(item.points)};
          return obj;
        })

        const data = {
          ...this.data,
          password: this.data.password ? this.data.password.toUpperCase() : null,
          prizePool: this.prizePoolToSend,
          winnerGamePlayerQuantity: this.data.winnerGamePlayersQuantity,
          requiredNumberOfPlayers: this.data.playerNumbers,
          quizRankingPoints: rankingPoints
        };

        let stagesParameters = [];
        for (let i in this.data.stagesParameters) {
          let t = this.$refs['ref-stage-' + i][0].getData();
          t.stageOrder = i * 1;

          delete t.cmsPagesList;

          stagesParameters.push(t);
        }
        data.stagesParameters = stagesParameters;
        this.recodeNumericValues(data);

        data.createdAt = moment(Date.now()).format('YYYY-MM-DD HH:mm:ss');

        delete data.prizePoolStructure;

        axios.post('/api/instant-tournament-waiting-room', data)
          .then(() => {
            setTimeout(() => {
              this.$parent.$emit('refreshGrid');
              this.$v.$reset();
              this.$data.data = this.getInitialData();
              this.prizePool = null;
              this.$refs['create-modal'].hide();
            }, 1000);
          }).catch(error => {
            console.log(error);
          });
      }
    },
    recodeNumericValues(data) {
      const parseStringToFloat = (value) => {
        if(!value) return value;
        return parseFloat(value.toString().replace(/,/g, '.'));
      }

      data.buyIn = parseStringToFloat(data.buyIn);
      data.finalStageParameters.pointsMultiplier = parseStringToFloat(data.finalStageParameters.pointsMultiplier);
      data.finalStageParameters.minAmountOfTime = parseStringToFloat(data.finalStageParameters.minAmountOfTime);
      data.finalStageParameters.numberOfRoundsToChangeTime = parseStringToFloat(data.finalStageParameters.numberOfRoundsToChangeTime);
      data.finalStageParameters.roundConfiguration.questionTimeBonus = parseStringToFloat(data.finalStageParameters.roundConfiguration.questionTimeBonus);
      data.finalStageParameters.roundConfiguration.questionTimePenalty = parseStringToFloat(data.finalStageParameters.roundConfiguration.questionTimePenalty);
      for (let i in data.stagesParameters) {
        data.stagesParameters[i].numberOfRounds = parseStringToFloat(data.stagesParameters[i].numberOfRounds);
        data.stagesParameters[i].roundConfiguration.timeForAnswer = parseStringToFloat(data.stagesParameters[i].roundConfiguration.timeForAnswer);
        data.stagesParameters[i].roundConfiguration.numberOfPoints = parseStringToFloat(data.stagesParameters[i].roundConfiguration.numberOfPoints);
        data.stagesParameters[i].roundConfiguration.pointsMultiplier = parseStringToFloat(data.stagesParameters[i].roundConfiguration.pointsMultiplier);
        data.stagesParameters[i].roundConfiguration.availableStakes =
            data.stagesParameters[i].roundConfiguration.availableStakes?.map( stake => parseStringToFloat(stake));

        if (data.stagesParameters[i].roundConfiguration.pointsToSteal) {
          data.stagesParameters[i].roundConfiguration.pointsToSteal = parseStringToFloat(data.stagesParameters[i].roundConfiguration.pointsToSteal);
        }

      }
    },
    getInitialData() {
      return {
        setNewRankingPoints: true,
        name: null,
        lobbyId: 'INSTANT_TOURNAMENT',
        questionCategoryId: null,
        maxGamePlayers: null,
        winnerGamePlayersQuantity: null,
        playerNumbers: null,
        prizePoolStructure: null,
        type: 'SINGLE_ELIMINATION',
        buyIn: null,
        walletCurrency: null,
        quizRankingPoints: [],
        stagesParameters: [],
        finalStageParameters: {
          pointsMultiplier: null,
          minAmountOfTime: null,
          numberOfRoundsToChangeTime: null,
          roundConfiguration: {
            questionTimeBonus: null,
            questionTimePenalty: null,
          },
          cmsPageKeyName: null
        },
        prizePool: null,
        password: null
      }
    },
    setData(data) {
      this.data = {
        ...data,
        ...data.gameParameters,
        id: data.id,
        lobbyId: 'INSTANT_TOURNAMENT',
        maxGamePlayers: data.gameParameters.maxPlayers,
        winnerGamePlayersQuantity: data.tournamentParameters.winnerGamePlayerQuantity,
        playerNumbers: data.requiredNumberOfPlayers,
        type: 'SINGLE_ELIMINATION'
      };

      const prizePool = [];
      const prizePollObject = this.data.prizePool;

      this.getPrizeStructure(true).then(() => {
        for (const property in prizePollObject) {
          prizePool.push({
            property: prizePollObject[property].position,
            value: prizePollObject[property].award,
            checked: prizePollObject[property].award > 0 ? true : false
          });
        }

        this.prizePool = prizePool;
      });

      this.$v.$reset();
    },
    showModal(){
      this.$v.$reset();
      this.$data.data = this.getInitialData();
      this.$refs['create-modal'].show();
    },
    resetData() {
      this.prizePool = null;
      this.data.prizePool = null;
      this.data = this.getInitialData();
    }
  }
}
</script>
